import type { TFunction } from 'i18next';
import type { U2000CriteriaStaticOption, U2000TableColumn } from '../../u2000Core/components/tables/basic/U2000BasicTableList';
import { U2000_CRITERIA_LOOKUP_U3130_FIND_EMPLOYEE } from '../../u2000Core/components/tables/commons/search/U2000CriteriaLookupRegistry';
import { formatCurrency } from '../../u2000Core/tools/U2000CurrencyUtils';
import type { U3130Timesheet } from '../U3130TimesheetModel';
import { u3130FormatPeriodDateRange } from './u3130FormatPeriodDateRange';

const NS = 'u3130';

/** Statuts admis en recherche U3130 (sans Ouvert — file + post-approbation). */
const U3130_TIMESHEET_STATUS_VALUES = ['WaitingForApproval', 'Approve', 'WaitingForGenerateTEC', 'Close'] as const;

const CURRENCY_DISPLAY_OPTS = {
  currencySymbol: '$',
  symbolPosition: 'left' as const,
  decimalSeparator: '.' as const,
  thousandSeparator: ' ' as const,
  decimalDigits: 2,
};

function getTimesheetStatusDisplay(t: TFunction, status: string | null | undefined): string {
  if (!status?.trim()) return '';
  return t(`status${status}`, { ns: NS, defaultValue: status });
}

export function u3130BuildTimesheetStatusCriteriaOptions(t: TFunction): U2000CriteriaStaticOption[] {
  return U3130_TIMESHEET_STATUS_VALUES.map((value) => ({
    value,
    label: t(`status${value}`, { ns: NS, defaultValue: value }),
  }));
}

export function u3130GetTimesheetListColumns(t: TFunction): U2000TableColumn[] {
  const statusOptions = u3130BuildTimesheetStatusCriteriaOptions(t);
  return [
    {
      name: 'periodDate',
      label: t('lblPeriodDate', { ns: NS }),
      widthClass: 'w-[280px]',
      type: 'date',
      align: 'left',
      inGridField: true,
      isDefaultColumn: true,
      isDefaultSortedColumn: true,
      defaultSortOrder: 'DESC',
      sortable: true,
      isCriteriaField: true,
      display: (_column, _value, row: U3130Timesheet) => {
        const r = row as unknown as Record<string, unknown>;
        const raw = r.periodDate ?? r.period_date;
        return u3130FormatPeriodDateRange(typeof raw === 'string' ? raw : '');
      },
    },
    {
      name: 'status',
      label: t('lblStatus', { ns: NS }),
      widthClass: 'w-[140px]',
      type: 'string',
      align: 'left',
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
      isCriteriaField: true,
      includeConditions: ['equals', 'in'],
      criteriaStaticOptions: statusOptions,
      display: (_column, value, row: U3130Timesheet) => {
        const rawStatus = typeof row.status === 'string' ? row.status : typeof value === 'string' ? value : '';
        return getTimesheetStatusDisplay(t, rawStatus);
      },
    },
    {
      name: 'employee',
      label: t('lblEmployee', { ns: NS }),
      widthClass: 'w-[200px]',
      type: 'string',
      align: 'left',
      inGridField: true,
      isDefaultColumn: true,
      display: (_column, _value, row: U3130Timesheet) => {
        const emp = row.employee;
        if (!emp) return '';
        const parts = [emp.firstName, emp.lastName].filter(Boolean);
        return parts.length ? parts.join(' ') : (emp.codeEmployee ?? row.employeeId ?? '');
      },
      isCriteriaField: false,
    },
    {
      name: 'codeEmployee',
      label: t('lblEmployee', { ns: NS, defaultValue: 'Employé' }),
      widthClass: 'w-[120px]',
      type: 'string',
      align: 'left',
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
      isCriteriaField: true,
      includeConditions: ['equals', 'in'],
      criteriaLookup: {
        findMethod: U2000_CRITERIA_LOOKUP_U3130_FIND_EMPLOYEE,
        searchCriterionField: 'codeEmployee',
        valueKey: 'codeEmployee',
        displayKeys: ['codeEmployee', 'firstName', 'lastName'],
        selectedDisplayKeys: ['codeEmployee', 'firstName', 'lastName'],
        placeholder: t('phCriteriaEmployee', { ns: 'common' }),
      },
      display: (_column, _value, row: U3130Timesheet) => row.employee?.codeEmployee?.trim() ?? '',
    },
    {
      name: 'totalPaidHours',
      label: t('lblTotalPaidHours', { ns: NS, defaultValue: 'Total projet' }),
      widthClass: 'w-[180px]',
      type: 'number',
      align: 'right',
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
      isCriteriaField: false,
      display: (_column, _value, row: U3130Timesheet) => {
        const total = row.totalPaidHours;
        return Number(total ?? 0).toFixed(2);
      },
    },
    {
      name: 'totalExpenses',
      label: t('lblTotalExpenses', { ns: NS, defaultValue: 'Total dépenses' }),
      widthClass: 'w-[150px]',
      type: 'number',
      align: 'right',
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
      isCriteriaField: false,
      display: (_column, _value, row: U3130Timesheet) => {
        const total = row.totalExpenses;
        if (typeof total !== 'number' || !Number.isFinite(total)) return '';
        return formatCurrency(total, CURRENCY_DISPLAY_OPTS);
      },
    },
    {
      name: 'totalEquipment',
      label: t('lblTotalEquipment', { ns: NS, defaultValue: 'Total équipement' }),
      widthClass: 'w-[150px]',
      type: 'number',
      align: 'right',
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
      isCriteriaField: false,
      display: (_column, _value, row: U3130Timesheet) => {
        const total = row.totalEquipment;
        if (typeof total !== 'number' || !Number.isFinite(total)) return '';
        return formatCurrency(total, CURRENCY_DISPLAY_OPTS);
      },
    },
  ];
}
